import React from "react";
import { compose } from "recompose";
import { withTranslation } from "react-i18next";
import { Flex } from "@chakra-ui/react";
import time from "src/utils/time";
import { Activities } from "src/modules/activity-log/activity";
import BouncingDotsLoader from "src/components/BouncingDotsLoader";
import { ResponsiveCaption } from "../../ResponsiveCaption";
import RoundButtonWithProgress from "./RoundButtonWithProgress";

const SHOW_LOADER_AFTER_DURATION = 200;

class CommandButton extends React.Component {
  state = {
    showLoader: false
  };
  replayStartTime = null;
  showLoaderTimeout = null;

  componentDidUpdate(prevProps, prevState) {
    const { showLoader } = this.state;
    const { status, initialReplayTime } = this.props;

    if (status === "loading") {
      if (this.showLoaderTimeout === null && showLoader === false) {
        this.showLoaderTimeout = setTimeout(() => {
          const { showLoader } = this.state;
          const { status } = this.props;
          if (status === "loading" && showLoader === false) {
            this.setState({ showLoader: true });
          }
        }, SHOW_LOADER_AFTER_DURATION);
      }
    } else if (this.showLoaderTimeout) {
      clearTimeout(this.showLoaderTimeout);
      this.showLoaderTimeout = null;
      this.setState({ showLoader: false });
    }

    if (status === "playing" && this.replayStartTime === null) {
      this.replayStartTime = Date.now() - initialReplayTime;
    }
    if (status !== "playing" && this.replayStartTime != null) {
      this.replayStartTime = null;
    }
  }
  componentWillUnmount() {
    const { status, stopCommand } = this.props;
    clearTimeout(this.showLoaderTimeout);
    status !== "resting" && stopCommand();
  }

  commandFinishedHandler = () => {
    const { resetCommandPlayer } = this.props;
    resetCommandPlayer();
  };

  getCurrentTime = () => {
    const { audioDuration } = this.props.command;
    const currentTime =
      this.replayStartTime == null ? 0 : Date.now() - this.replayStartTime;
    return Math.min(audioDuration, currentTime);
  };

  getProgress = () => {
    const { audioDuration } = this.props.command;
    const currentTime = this.getCurrentTime();
    return currentTime / audioDuration;
  };

  render() {
    const { showLoader } = this.state;
    const {
      t,
      isDarkMode,
      mode,
      activity,
      command,
      root,
      playCommand,
      stopCommand,
      status
    } = this.props;
    const { icon, name, color } = command;

    return (
      <Flex flexDirection="column" alignItems="center" {...root}>
        <RoundButtonWithProgress
          status={status}
          onClick={status === "resting" ? playCommand : stopCommand}
          isDarkMode={isDarkMode}
          color={color}
          icon={icon}
          activity={activity}
          mode={mode}
          getProgress={this.getProgress}
          onFinish={this.commandFinishedHandler}
        />
        <ResponsiveCaption
          mt="4px"
          lineHeight="1.6"
          height={[`${1.6 * 10}px`, `${1.6 * 14}px`, `${1.6 * 16}px`]}
          color={
            isDarkMode || activity === Activities.AWAKE || mode === "video"
              ? "white70"
              : "black54"
          }
        >
          {status === "playing" ? (
            <CurrentTime getCurrentTime={this.getCurrentTime} />
          ) : showLoader ? (
            <BouncingDotsLoader bg="mediumEmphasis" mt="8px" />
          ) : (
            name || t(`command.name.${icon}`)
          )}
        </ResponsiveCaption>
      </Flex>
    );
  }
}

function CurrentTime({ getCurrentTime }) {
  const [currentTime, setCurrentTime] = React.useState(getCurrentTime());

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 60);
    return () => clearInterval(intervalId);
  }, [getCurrentTime]);

  return time.duration(currentTime).format("mm:ss", { stopTrim: "m" });
}

const enhance = compose(withTranslation());

export default enhance(CommandButton);
